'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { api } from '@/lib/api'
import { DEFAULT_SITE_ID } from '@/app/api/base'
import type { FlowAttachment } from '@/types/flow'

export type FlowUserInputConfig = {
  type: 'text' | 'number' | 'email' | 'phone' | 'date'
  variableName: string
  placeholder?: string
  required?: boolean
  validation?: {
    minLength?: number
    maxLength?: number
    pattern?: string
    errorMessage?: string
  }
}

export interface FlowExecutionResult {
  success: boolean
  error?: string
  shouldRetry?: boolean
  message?: string
  attachments?: FlowAttachment[]
  paymentRequest?: {
    nodeId: string
    amount: number | string
    currency: string
    description: string
    providerType: string
    paymentUrl?: string
    transactionId?: string
    expiresAt?: string | Date
    buttonText?: string
  }
  openUrl?: {
    url: string
    label?: string
    preferEmbed?: boolean
  }
  nextNodeId?: string
  nextNodeType?: string
  nextIsInteractive?: boolean
  flowComplete?: boolean
  shouldRestart?: boolean
  requiresBotResponse?: boolean
  restartDelay?: number
  redirectFlowId?: number
  redirectFlowName?: string
  useNlpRedirect?: boolean
  botAssignment?: {
    botId: number
    transferType: 'full' | 'context'
    teamId?: number | null
  }
  botAssignmentInitialMessage?: string
  agentHandover?: {
    teamId?: number | null
    teamName?: string | null
    specificAgentId?: number
    priority: 'low' | 'normal' | 'high' | 'urgent'
    message: string
    sendToAllAgents?: boolean
    autoAssign?: boolean
  }
  menuOptions?: {
    id: string
    label: string
    value: string
    url?: string
  }[]
  userInput?: boolean | FlowUserInputConfig
  formNode?: {
    nodeId: string
    config: {
      message: string
      fields: {
        id: string
        label: string
        inputType:
          | 'text'
          | 'phone'
          | 'email'
          | 'textarea'
          | 'number'
          | 'password'
          | 'toggle'
          | 'select'
          | 'checkbox'
        variableName: string
        placeholder?: string
        required?: boolean
      }[]
      formName?: string
      formVariableName?: string
      submitButtonText?: string
      cancelButtonText?: string
      cancelActionValue?: string
      allowSkip?: boolean
      skipButtonText?: string
      skipActionValue?: string
    }
  }
  botNodeState?: {
    nodeId: string
    nextNodeId: string | null
    instructionPrompt?: string | null
    waitingForEndTool?: boolean
  }
  // Entry node properties
  openChat?: boolean // Auto-open chat when this entry node triggers
}

export interface FlowMessagePayload {
  message?: string
  attachments?: FlowAttachment[]
  paymentRequest?: FlowExecutionResult['paymentRequest']
  type?: 'message' | 'form'
  content?: string
  formNode?: any
}

export interface FlowSession {
  id: string
  sessionId: string
  flowId: number
  flowName?: string
  currentNodeId?: string
  flowData?: Record<string, any>
  startedAt: string
  updatedAt: string
}

interface UseFlowExecutionProps {
  sessionId: string | null
  /**
   * @deprecated Site scoping is handled server-side via default site configuration.
   * This is ignored but kept for backward compatibility with existing callers/tests.
   */
  siteId?: string
  isHandedOver?: boolean
  sessionStatusChecked?: boolean
  onBotAssignment?: (
    botId: number,
    transferType: 'full' | 'context',
    teamId?: number | null,
    requiresBotResponse?: boolean
  ) => void | Promise<void>
  onAgentHandover?: (handoverConfig: any) => void
  onFlowComplete?: () => void
  onFlowMessage?: (payload: FlowMessagePayload) => void
  onMenuOptions?: (
    message: string,
    options: { id: string; label: string; value: string }[]
  ) => void
  onOpenChat?: () => void // Callback to auto-open chat when entry node has openChat enabled
  isUserRegistered?: boolean
  startFlowId?: number | null
  startFlowSlug?: string | null
}

export function useFlowExecution({
  sessionId,
  isHandedOver = false,
  sessionStatusChecked = false,
  onBotAssignment,
  onAgentHandover,
  onFlowComplete,
  onFlowMessage,
  onMenuOptions,
  onOpenChat,
  isUserRegistered = true,
  startFlowId,
  startFlowSlug,
}: UseFlowExecutionProps) {
  const resolvedSiteId = DEFAULT_SITE_ID
  const flowOverrideId =
    typeof startFlowId === 'number' &&
    Number.isFinite(startFlowId) &&
    startFlowId > 0
      ? startFlowId
      : null
  const flowOverrideSlug =
    typeof startFlowSlug === 'string' && startFlowSlug.trim().length > 0
      ? startFlowSlug.trim()
      : null
  const [isFlowActive, setIsFlowActive] = useState(false)
  const [flowSession, setFlowSession] = useState<FlowSession | null>(null)

  // Normalize arbitrary API payloads to FlowSession or null to satisfy typings
  const normalizeFlowSession = (value: any): FlowSession | null => {
    if (!value || typeof value !== 'object') return null
    const id = value.id != null ? String(value.id) : undefined
    const sessionIdVal =
      value.sessionId != null ? String(value.sessionId) : undefined
    const flowIdVal = value.flowId != null ? Number(value.flowId) : undefined
    if (!id || !sessionIdVal || !Number.isFinite(flowIdVal)) return null

    const normalized: FlowSession = {
      id,
      sessionId: sessionIdVal,
      flowId: Number(flowIdVal),
      flowName: typeof value.flowName === 'string' ? value.flowName : undefined,
      currentNodeId:
        value.currentNodeId != null ? String(value.currentNodeId) : undefined,
      flowData:
        typeof value.flowData === 'string'
          ? value.flowData
            ? JSON.parse(value.flowData)
            : {}
          : value.flowData || {},
      startedAt: value.startedAt
        ? String(value.startedAt)
        : new Date().toISOString(),
      updatedAt: value.updatedAt
        ? String(value.updatedAt)
        : new Date().toISOString(),
    }

    return normalized
  }

  const [isProcessingFlow, setIsProcessingFlow] = useState(false)
  // Track processing flag in a ref to coordinate follow-up actions
  const isProcessingFlowRef = useRef(false)
  useEffect(() => {
    isProcessingFlowRef.current = isProcessingFlow
  }, [isProcessingFlow])

  // Helper: wait until the current executeFlow finishes (or timeout)
  const waitUntilIdle = async (timeoutMs = 2000) => {
    const start = Date.now()
    while (isProcessingFlowRef.current) {
      await new Promise(resolve => setTimeout(resolve, 50))
      if (Date.now() - start > timeoutMs) break
    }
  }

  const [flowError, setFlowError] = useState<string | null>(null)
  const [hasExecutedEntry, setHasExecutedEntry] = useState(false)
  const [isFlowCompleting, setIsFlowCompleting] = useState(false)
  const [lastCompletionTime, setLastCompletionTime] = useState<number>(0)
  const [lastSessionId, setLastSessionId] = useState<string | null>(null)
  const [isExecutingEntry, setIsExecutingEntry] = useState(false)
  const [lastUserMessage, setLastUserMessage] = useState<string>('')
  const [userMessageHistory, setUserMessageHistory] = useState<string[]>([])
  const [hasDefaultFlow, setHasDefaultFlow] = useState<boolean | null>(null)
  const [defaultBot, setDefaultBot] = useState<{
    id: number
    name: string
    slug: string
    status?: number
  } | null>(null)

  // Keep a stable ref to the onFlowMessage callback so we don't recreate
  // emitFlowMessage whenever the parent passes a new function instance.
  const onFlowMessageRef = useRef<typeof onFlowMessage | null>(null)
  useEffect(() => {
    onFlowMessageRef.current = onFlowMessage ?? null
  }, [onFlowMessage])

  const emitFlowMessage = useCallback(
    (
      messageText: string,
      attachments?: FlowAttachment[],
      paymentRequest?: FlowExecutionResult['paymentRequest']
    ) => {
      const handler = onFlowMessageRef.current
      if (!handler) {
        return
      }
      const normalizedMessage = messageText ?? ''
      const hasMessage = normalizedMessage.trim().length > 0
      const hasAttachments =
        Array.isArray(attachments) && attachments.length > 0

      if (!hasMessage && !hasAttachments) {
        return
      }

      handler({
        message: hasMessage ? normalizedMessage : '',
        attachments: hasAttachments ? attachments : undefined,
        paymentRequest,
      })
    },
    []
  )

  const hasDisplayContent = (
    messageText?: string,
    attachments?: FlowAttachment[]
  ) => {
    const hasMessage =
      typeof messageText === 'string' && messageText.trim().length > 0
    const hasAttachments = Array.isArray(attachments) && attachments.length > 0
    return hasMessage || hasAttachments
  }

  // Track URLs already opened this session to avoid duplicate tabs on re-renders.
  const openedUrlsRef = useRef<Set<string>>(new Set())

  const combineMessageWithLink = useCallback(
    (
      messageText?: string,
      openUrl?: FlowExecutionResult['openUrl']
    ): string => {
      if (openUrl?.url) {
        // Open the URL once per unique URL per session. Subsequent calls
        // (e.g. from re-renders or retries) skip window.open but still
        // return the URL as fallback text so the user can tap it manually
        // if the popup was blocked.
        if (!openedUrlsRef.current.has(openUrl.url)) {
          openedUrlsRef.current.add(openUrl.url)
          try {
            window.open(openUrl.url, '_blank', 'noopener,noreferrer')
          } catch {
            // popup blocked — URL visible in message text below
          }
        }
        const base = messageText?.trim() ?? ''
        return base ? `${base}\n${openUrl.url}` : openUrl.url
      }
      return messageText?.trim() ?? ''
    },
    []
  )

  const buildDisplayMessage = useCallback(
    (result: {
      message?: string
      openUrl?: FlowExecutionResult['openUrl']
    }): string => combineMessageWithLink(result?.message, result?.openUrl),
    [combineMessageWithLink]
  )

  const canAutoProgress = (result: any) => {
    if (!result?.nextNodeId || result.flowComplete) {
      return false
    }

    // If backend explicitly says we must wait for user input, do not auto-progress.
    if ((result as any).userInput === true) {
      return false
    }

    // If backend explicitly says it's safe to auto-progress, do so.
    if ((result as any).userInput === false) {
      return true
    }

    // Prefer the backend-provided `nextIsInteractive` for new/custom node types.
    if (typeof result.nextIsInteractive === 'boolean') {
      return result.nextIsInteractive === false
    }

    // Fallback for older servers that only provided `nextNodeType`.
    return (
      result.nextNodeType === 'conditional' ||
      result.nextNodeType === 'action' ||
      result.nextNodeType === 'end' ||
      result.nextNodeType === 'bot_assignment' ||
      result.nextNodeType === 'agent_handover' ||
      (result.nextNodeType === 'message' &&
        (result as any).userInput === false) ||
      (result.nextNodeType === 'menu' && (result as any).userInput === false) ||
      (result.nextNodeType === 'form' && (result as any).userInput === false)
    )
  }

  const autoProgressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )

  useEffect(() => {
    return () => {
      if (autoProgressTimeoutRef.current) {
        clearTimeout(autoProgressTimeoutRef.current)
        autoProgressTimeoutRef.current = null
      }
    }
  }, [])

  // Use ref to track entry execution across re-renders
  const entryExecutionRef = useRef<{
    isExecuting: boolean
    executedForSession: string | null
  }>({
    isExecuting: false,
    executedForSession: null,
  })

  // When true, we intentionally avoid auto-(re)starting flows after an Exit end node
  const flowExitModeRef = useRef<boolean>(false)

  // Use ref to prevent multiple initializations
  const initializationRef = useRef<{
    isInitializing: boolean
    initializedForSession: string | null
  }>({
    isInitializing: false,
    initializedForSession: null,
  })

  type ExecuteFlowFn = (
    userInput?: string,
    nodeId?: string,
    displayText?: string
  ) => Promise<FlowExecutionResult | null>

  const executeFlowRef = useRef<ExecuteFlowFn | null>(null)

  const runExecuteFlow = useCallback<ExecuteFlowFn>(
    async (userInput, nodeId, displayText) => {
      if (!executeFlowRef.current) {
        console.warn(
          '⚠️ [FLOW] executeFlow called before initialization - skipping'
        )
        return null
      }
      return executeFlowRef.current(userInput, nodeId, displayText)
    },
    []
  )

  // Trigger entry node for existing session
  const triggerEntryNodeForExistingSession = useCallback(async () => {
    if (
      !sessionId ||
      !flowSession ||
      isExecutingEntry ||
      entryExecutionRef.current.isExecuting
    ) {
      return
    }

    const alreadyExecutedForThisSession =
      entryExecutionRef.current.executedForSession === sessionId
    if (alreadyExecutedForThisSession) {
      console.log('🚫 [FLOW] Entry already executed for this session')
      return
    }

    try {
      console.log(
        '🎬 [FLOW] Triggering entry node for existing session:',
        sessionId
      )

      // Set execution state
      entryExecutionRef.current.isExecuting = true
      entryExecutionRef.current.executedForSession = sessionId
      setIsExecutingEntry(true)

      let triggerRespRaw = await api.flows.trigger({
        sessionId,
        triggerContext: 'chat_initialize',
      })
      console.log(
        '🔍 [FLOW] Trigger API status:',
        triggerRespRaw.success ? 'success' : 'failed'
      )
      if (!triggerRespRaw.success) {
        console.error('❌ [FLOW] Trigger API error:', triggerRespRaw)
      }
      let triggerPayload: any = triggerRespRaw

      // Fallback: if no chat_initialize entry exists, try flow_start immediately
      if (!triggerPayload?.success || !triggerPayload?.data) {
        console.log(
          '↩️ [FLOW] No chat_initialize entry, retrying trigger with flow_start (existing session)'
        )
        triggerRespRaw = await api.flows.trigger({
          sessionId,
          triggerContext: 'flow_start',
        })
        console.log(
          '🔍 [FLOW] Trigger API status (flow_start fallback):',
          triggerRespRaw.success ? 'success' : 'failed'
        )
        if (!triggerRespRaw.success) {
          console.error(
            '❌ [FLOW] Trigger API error (fallback):',
            triggerRespRaw
          )
        }
        triggerPayload = triggerRespRaw
      }

      if (triggerPayload && triggerPayload.success && triggerPayload.data) {
        const result = triggerPayload.data
        if (result.message) {
          // Do NOT push entry message here; it will already be present from chat history.
          setHasExecutedEntry(true)
          console.log(
            '✅ [FLOW] Entry node executed successfully for existing session:',
            sessionId
          )
        }

        // Handle auto-open chat
        if (result.openChat) {
          console.log(
            '💬 [FLOW] Entry node requested auto-open chat (existing session)'
          )
          onOpenChat?.()
        }
        // Auto-continue from Entry.
        // Prefer the explicit outgoing connection from Entry; if none, fall back to the flow's current start node.
        if (result?.nextNodeId) {
          setFlowSession(prev =>
            prev ? { ...prev, currentNodeId: result.nextNodeId } : prev
          )

          // Check if Entry node wants to wait for user input
          if ((result as any).userInput === true) {
            console.log(
              '⏸️ [FLOW] Entry node (existing session) has autoProgress OFF - waiting for user input before proceeding to:',
              result.nextNodeId
            )
            // Don't auto-progress - wait for user input
          } else {
            console.log(
              '▶️ [FLOW] Continuing from Entry (existing session) to next node:',
              result.nextNodeId
            )
            await runExecuteFlow('', result.nextNodeId)
          }
        } else if (flowSession?.currentNodeId) {
          console.log(
            '▶️ [FLOW] Entry has no outgoing connection; continuing from flow start node (existing session):',
            flowSession.currentNodeId
          )
          await runExecuteFlow('', flowSession.currentNodeId)
        }
      }
    } catch (error) {
      console.log(
        '❌ [FLOW] Failed to trigger entry node for existing session:',
        error
      )
      entryExecutionRef.current.executedForSession = null // Reset on error
    } finally {
      entryExecutionRef.current.isExecuting = false
      setIsExecutingEntry(false)
    }
  }, [flowSession, isExecutingEntry, onOpenChat, runExecuteFlow, sessionId])

  // Auto-cleanup stale flow sessions when default flow changes
  const cleanupStaleFlowSessions = useCallback(
    async (currentDefaultFlowId: number) => {
      try {
        console.log('🧹 [FLOW] Checking for stale flow sessions to cleanup...')

        // Get current flow sessions for this chat session
        if (!sessionId) return
        const sessionsResp = await api.flows.getSessions(sessionId)
        const sessionsPayload: any =
          (sessionsResp && (sessionsResp as any).data) ?? sessionsResp

        if (sessionsPayload) {
          const flowSessions = Array.isArray(sessionsPayload)
            ? sessionsPayload
            : [sessionsPayload]

          // Find sessions that are not for the current default flow
          const staleSessions = flowSessions.filter(
            (session: any) => session.flowId !== currentDefaultFlowId
          )

          if (staleSessions.length > 0) {
            console.log(
              `🧹 [FLOW] Found ${staleSessions.length} stale flow sessions, cleaning up...`
            )

            // Delete stale flow sessions
            for (const staleSession of staleSessions) {
              try {
                await api.flows.deleteSession(staleSession.id)
                console.log(
                  `✅ [FLOW] Cleaned up stale flow session: ${staleSession.id} (flow ${staleSession.flowId})`
                )
              } catch (deleteError) {
                console.warn(
                  `⚠️ [FLOW] Failed to cleanup stale session ${staleSession.id}:`,
                  deleteError
                )
              }
            }
          } else {
            console.log('✅ [FLOW] No stale flow sessions found')
          }
        }
      } catch (error) {
        console.warn('⚠️ [FLOW] Failed to cleanup stale flow sessions:', error)
        // Don't throw - this is a cleanup operation that shouldn't break flow initialization
      }
    },
    [sessionId]
  )

  // Check for available flows and reset state if none found
  const checkAvailableFlows = useCallback(async () => {
    if (!resolvedSiteId) {
      console.warn(
        '🔍 [FLOW] Skipping available flow check - default site ID missing'
      )
      setHasDefaultFlow(false)
      return
    }
    try {
      console.log('🔍 [FLOW] Checking available flows for default site')
      const activeFlowResp = await api.flows.getActiveFlow()
      console.log(
        '🔍 [FLOW] API status:',
        activeFlowResp?.success ? 'success' : 'failed'
      )
      if (!activeFlowResp?.success) {
        console.error('❌ [FLOW] API error:', {
          success: activeFlowResp?.success ?? false,
          status: activeFlowResp?.status,
          error: activeFlowResp?.error,
          message: activeFlowResp?.message,
        })
      }
      const activeFlow = activeFlowResp?.data
      console.log('🔍 [FLOW] Active flow data:', activeFlow)

      // Check if we have a default flow that's active/enabled
      if (
        activeFlow &&
        activeFlow.name &&
        activeFlow.id &&
        activeFlow.isActive !== false
      ) {
        console.log('✅ [FLOW] Found valid default flow:', {
          id: activeFlow.id,
          name: activeFlow.name,
          isActive: activeFlow.isActive,
        })
        setHasDefaultFlow(true)
      } else {
        console.log(
          '❌ [FLOW] No default flow found. Will check during initialization if flows are available.'
        )
        // Don't set hasDefaultFlow yet - wait for initialization response
        // Clear flow state since no default flow is active
        setIsFlowActive(false)
        setFlowSession(null)
        setFlowError(null)
      }
    } catch (error) {
      console.error('❌ [FLOW] Error checking flows:', error)
      setHasDefaultFlow(false)
    }
  }, [resolvedSiteId])

  // Check for default bot tied to the default site configuration
  const checkDefaultBot = useCallback(async () => {
    if (!resolvedSiteId) {
      setDefaultBot(null)
      return
    }

    try {
      console.log('🤖 [BOT] Fetching default bot for default site')
      const botResp = await api.publicBots.getDefault()
      const botPayload: any = botResp?.data ?? null
      const botData: any = botPayload?.data ?? botPayload

      if (!botResp?.success || !botData) {
        console.log('🤖 [BOT] No active default bot found for default site')
        setDefaultBot(null)
        return
      }

      setDefaultBot({
        id: botData.id,
        name: botData.name || 'AI Assistant',
        slug: botData.slug || 'default-assistant',
        status: typeof botData.status === 'number' ? botData.status : undefined,
      })
    } catch (error) {
      console.error('❌ [BOT] Failed to fetch default bot:', error)
      setDefaultBot(null)
    }
  }, [resolvedSiteId])

  // Initialize flow session when chat session is created
  const initializeFlow = useCallback(
    async (forceRestart = false) => {
      if (!sessionId) {
        // Still check for available flows to update the indicator
        await checkAvailableFlows()
        return
      }

      if (!isUserRegistered) {
        console.log(
          '⏸️ [FLOW] Waiting for user registration before initializing flow'
        )
        await checkAvailableFlows()
        return
      }

      // If we previously exited this flow, skip auto-initialization unless explicitly forced
      if (!forceRestart && flowExitModeRef.current) {
        console.log(
          '🚪 [FLOW] Flow was exited; skipping auto-initialization until user explicitly restarts'
        )
        return
      }

      // Prevent multiple simultaneous initializations for the same session
      if (!forceRestart && initializationRef.current.isInitializing) {
        console.log(
          '🚫 [FLOW] Initialization already in progress, skipping duplicate'
        )
        return
      }

      if (
        !forceRestart &&
        initializationRef.current.initializedForSession === sessionId
      ) {
        console.log(
          '🚫 [FLOW] Already initialized for this session, skipping duplicate'
        )
        return
      }

      // Mark as initializing
      initializationRef.current.isInitializing = true
      console.log(
        '🔒 [FLOW] Marked initialization as in progress for session:',
        sessionId
      )

      // If restart is explicitly requested, clear any previous Exit suppression
      if (forceRestart) {
        flowExitModeRef.current = false
      }

      // Prevent re-initialization if flow is already active, completing, or recently completed
      const timeSinceCompletion = Date.now() - lastCompletionTime
      const isInCooldown = timeSinceCompletion < 2000 // 2 second cooldown

      // For flow initialization, we don't need to wait for session status check
      // The entry node should execute immediately when chat opens
      console.log(
        '✅ [FLOW] Proceeding with flow initialization (sessionStatusChecked not required for flows)'
      )

      console.log(
        '✅ [FLOW] Session status checked, proceeding with flow initialization'
      )

      // Debug: Log current state during initialization
      console.log('🔍 [FLOW] Current state during initialization:', {
        isFlowActive,
        hasFlowSession: !!flowSession,
        isFlowCompleting,
        isProcessingFlow,
        isExecutingEntry,
        isHandedOver,
        isInCooldown,
      })

      // Skip initialization if session is handed over to agent
      if (isHandedOver) {
        console.log(
          '🚫 [FLOW] Skipping initialization - session handed over to agent'
        )
        setIsFlowActive(false)
        setFlowSession(null)
        return
      }

      // Skip initialization if already processing or if we have a valid flow session
      // BUT allow restart to force re-initialization
      if (
        !forceRestart &&
        ((isFlowActive &&
          flowSession &&
          flowSession.sessionId === sessionId &&
          !isInCooldown) ||
          isFlowCompleting ||
          isProcessingFlow ||
          isExecutingEntry)
      ) {
        // Even if we skip initialization, check if we need to execute entry node for existing session
        // But only if this is truly a page refresh scenario, not a fresh initialization
        if (
          isFlowActive &&
          flowSession &&
          !isExecutingEntry &&
          !entryExecutionRef.current.isExecuting &&
          hasExecutedEntry === false
        ) {
          const alreadyExecutedForThisSession =
            entryExecutionRef.current.executedForSession === sessionId
          const isPageRefreshScenario =
            !alreadyExecutedForThisSession && flowSession.currentNodeId

          console.log(
            '🤔 [FLOW] Checking if should trigger entry for existing session:',
            {
              alreadyExecutedForThisSession,
              isPageRefreshScenario,
              hasExecutedEntry,
              currentNodeId: flowSession.currentNodeId,
            }
          )

          if (!alreadyExecutedForThisSession && isPageRefreshScenario) {
            setTimeout(() => {
              triggerEntryNodeForExistingSession()
            }, 100)
          } else {
            console.log(
              '🔇 [FLOW] Skipping entry trigger for existing session - already executed or not page refresh'
            )
          }
        }

        return
      }

      try {
        setFlowError(null)

        let initRespRaw: any = null
        let usedFlowOverride = false

        if (flowOverrideId || flowOverrideSlug) {
          if (flowOverrideId) {
            console.log(
              '🎯 [FLOW] Using override flow ID from URL:',
              flowOverrideId
            )
            await cleanupStaleFlowSessions(flowOverrideId)
          } else {
            console.log(
              '🎯 [FLOW] Using override flow slug from URL:',
              flowOverrideSlug
            )
          }

          const overrideResp = await api.flows.initialize({
            sessionId,
            triggerContext: 'flow_redirect',
            userMessage: flowOverrideId
              ? `ID:${flowOverrideId}`
              : (flowOverrideSlug ?? ''),
          })
          console.log(
            '🔍 [FLOW] Initialize API status (override):',
            overrideResp?.success ? 'success' : 'failed'
          )
          if (!overrideResp?.success) {
            console.error('❌ [FLOW] Initialize API error (override):', {
              success: overrideResp?.success,
              error: overrideResp?.error,
              message: overrideResp?.message,
            })
          }
          if (overrideResp?.success && overrideResp.data) {
            initRespRaw = overrideResp
            usedFlowOverride = true
          } else {
            console.warn(
              '⚠️ [FLOW] Override flow initialization failed; falling back to default flow'
            )
          }
        }

        if (!initRespRaw) {
          // Check if there's a default flow for this site
          const flowResp = await api.flows.getActiveFlow()
          const flowData: any = (flowResp && (flowResp as any).data) ?? flowResp

          if (flowData) {
            const activeFlow = flowData
            const isFlowEnabled =
              !!activeFlow && !!activeFlow.id && activeFlow.isActive !== false

            if (!isFlowEnabled) {
              console.log(
                '🚫 [FLOW] Default flow is inactive or missing required fields, skipping initialization'
              )
              setIsFlowActive(false)
              setFlowSession(null)
              setHasDefaultFlow(false)
              setFlowError(null)

              // Mark initialization as complete so subsequent checks can re-run when state changes
              initializationRef.current.isInitializing = false
              initializationRef.current.initializedForSession = null
              return
            }

            setHasDefaultFlow(true)

            // Auto-cleanup stale flow sessions before initializing new flow
            console.log(
              '🧹 [FLOW] About to cleanup stale flow sessions for flow:',
              activeFlow.id
            )
            await cleanupStaleFlowSessions(activeFlow.id)
            console.log('🧹 [FLOW] Stale flow sessions cleanup completed')

            // Initialize flow session with chat_initialize trigger
            console.log('🔄 [FLOW] About to call initialize API...')
            const initRespRawDefault = await api.flows.initialize({
              sessionId,
              triggerContext: 'chat_initialize',
            })
            console.log(
              '🔍 [FLOW] Initialize API status:',
              initRespRawDefault?.success ? 'success' : 'failed'
            )
            if (!initRespRawDefault?.success) {
              console.error('❌ [FLOW] Initialize API error:', {
                success: initRespRawDefault?.success,
                error: initRespRawDefault?.error,
                message: initRespRawDefault?.message,
              })
              setFlowError(
                initRespRawDefault?.error ||
                  initRespRawDefault?.message ||
                  'Failed to initialize flow'
              )
            }
            initRespRaw = initRespRawDefault
          } else {
            // No default flow found - check if there are any active flows
            console.log('🚫 [FLOW] No default flow found for default site')

            // For now, just set hasDefaultFlow to false and let the chat system handle LLM routing
            // when user sends their first message
            console.log(
              '⏳ [FLOW] No default flow - will wait for user input to trigger LLM routing'
            )
            setHasDefaultFlow(false)
            initializationRef.current.isInitializing = false
            initializationRef.current.initializedForSession = null
            // Don't set isFlowActive yet - wait for user input
            return
          }
        }

        const entryTriggerContext = usedFlowOverride
          ? 'flow_start'
          : 'chat_initialize'
        const entryFallbackContext = usedFlowOverride
          ? 'chat_initialize'
          : 'flow_start'
        const initPayload: any = initRespRaw

        if (initPayload && initPayload.success && initPayload.data) {
          const flowSessionData = initPayload.data

          // Check if the flow session itself has an error
          if (flowSessionData.success === false || flowSessionData.error) {
            console.log(
              '❌ [FLOW] Flow session has error (flow likely disabled):',
              flowSessionData.error
            )
            setFlowSession(null)
            setIsFlowActive(false)
            setHasDefaultFlow(false)
            // Don't set flowError for disabled flows - this is expected behavior
            setFlowError(null)
            console.log('✅ [FLOW] Flow disabled - AI assistant available!')
            return
          }

          setFlowSession(normalizeFlowSession(flowSessionData))
          setIsFlowActive(true)
          setHasDefaultFlow(true)

          // Execute entry node with the selected entry trigger
          const flowSession = initPayload.data
          const flowData =
            typeof flowSession?.flowData === 'string'
              ? JSON.parse(flowSession.flowData || '{}')
              : flowSession?.flowData || {}

          // Check if we should trigger entry node using ref for persistence
          const isAtStartNode =
            !flowData.lastExecutedNode ||
            flowData.resetReason === 'restart_after_exit'
          const alreadyExecutedForThisSession =
            entryExecutionRef.current.executedForSession === sessionId
          const isCurrentlyExecuting = entryExecutionRef.current.isExecuting

          // Only check for executed nodes if we're not in a reset scenario
          const hasExecutedNodes =
            flowData.resetReason !== 'restart_after_exit' &&
            (flowData.lastExecutedNode ||
              (flowData.currentStep && flowData.currentStep > 0))

          // Check if this is a page refresh scenario (frontend state lost but backend session exists)
          // Page refresh indicators:
          // 1. Frontend hasn't executed for this session yet (!alreadyExecutedForThisSession)
          // 2. Backend has a flow session with a current node (indicating flow was previously active)
          const isPageRefresh =
            !alreadyExecutedForThisSession && flowSession.currentNodeId

          // Trigger entry node if:
          // 1. At start node and not executed yet, OR
          // 2. Page refresh scenario (frontend lost state but backend has session)
          // 3. AND we haven't already executed entry for this session
          // 4. OR if this is a forced restart (bypass hasExecutedEntry check)
          const shouldTriggerEntry =
            (forceRestart || !hasExecutedEntry) && // Allow restart to bypass hasExecutedEntry
            ((isAtStartNode &&
              !alreadyExecutedForThisSession &&
              !isCurrentlyExecuting &&
              !hasExecutedNodes) ||
              (isPageRefresh && !isCurrentlyExecuting))

          console.log('🔍 [FLOW] Entry trigger conditions:', {
            forceRestart,
            hasExecutedEntry,
            isAtStartNode,
            alreadyExecutedForThisSession,
            isCurrentlyExecuting,
            hasExecutedNodes,
            isPageRefresh,
            shouldTriggerEntry,
            currentNodeId: flowSession.currentNodeId,
            executedForSession: entryExecutionRef.current.executedForSession,
          })

          if (shouldTriggerEntry) {
            try {
              console.log(
                '🎬 [FLOW] Triggering entry node for session:',
                sessionId
              )

              // Double-check: Make sure we haven't already executed for this session
              console.log('🔍 [FLOW] Checking if entry already executed:', {
                executedForSession:
                  entryExecutionRef.current.executedForSession,
                currentSessionId: sessionId,
                isAlreadyExecuted:
                  entryExecutionRef.current.executedForSession === sessionId,
              })

              if (entryExecutionRef.current.executedForSession === sessionId) {
                console.log(
                  '🚫 [FLOW] Entry already executed for this session, aborting'
                )
                return
              }

              // Set execution state in ref (persists across re-renders)
              entryExecutionRef.current.isExecuting = true
              entryExecutionRef.current.executedForSession = sessionId // Mark immediately to prevent race conditions
              setIsExecutingEntry(true) // Also set state for UI

              console.log(
                '🔒 [FLOW] Marked entry as executed for session:',
                sessionId
              )

              let triggerRespRaw = await api.flows.trigger({
                sessionId,
                triggerContext: entryTriggerContext,
              })
              console.log(
                '🔍 [FLOW] Trigger API response (new session):',
                triggerRespRaw
              )

              // Fallback: if no entry exists, try the alternate trigger context
              if (!triggerRespRaw?.success || !triggerRespRaw?.data) {
                console.log(
                  '↩️ [FLOW] Entry trigger failed, retrying with fallback context (new session)'
                )
                triggerRespRaw = await api.flows.trigger({
                  sessionId,
                  triggerContext: entryFallbackContext,
                })
                console.log(
                  '🔍 [FLOW] Trigger API response (fallback):',
                  triggerRespRaw
                )
              }
              const triggerPayload: any = triggerRespRaw

              if (
                triggerPayload &&
                triggerPayload.success &&
                triggerPayload.data
              ) {
                const result = triggerPayload.data
                if (
                  !forceRestart &&
                  hasDisplayContent(
                    buildDisplayMessage(result),
                    result.attachments
                  )
                ) {
                  setHasExecutedEntry(true)
                  emitFlowMessage(
                    buildDisplayMessage(result) ?? '',
                    result.attachments,
                    result.paymentRequest
                  )
                  console.log(
                    '✅ [FLOW] Entry node executed - delivered first message immediately for session:',
                    sessionId
                  )
                } else if (forceRestart) {
                  console.log(
                    '🔇 [FLOW] Suppressing immediate Entry message push on restart to avoid duplicates'
                  )
                }

                // Handle auto-open chat from entry nodes
                if (result.openChat) {
                  console.log('💬 [FLOW] Entry node requested auto-open chat')
                  onOpenChat?.()
                }

                // Update flow session with the new current node after Entry Node execution
                if (result.nextNodeId) {
                  setFlowSession(prev =>
                    prev
                      ? {
                          ...prev,
                          currentNodeId: result.nextNodeId,
                        }
                      : prev
                  )
                  console.log(
                    '🔄 [FLOW] Updated session currentNodeId to:',
                    result.nextNodeId
                  )

                  // Debug: Log the exact userInput value received from backend
                  console.log(
                    '🔍 [FRONTEND] Entry node result received:',
                    JSON.stringify(
                      {
                        userInput: (result as any).userInput,
                        nextNodeId: result.nextNodeId,
                        nextNodeType: result.nextNodeType,
                        message: result.message,
                      },
                      null,
                      2
                    )
                  )

                  // Check if Entry node wants to wait for user input
                  if ((result as any).userInput === true) {
                    console.log(
                      '⏸️ [FRONTEND] Entry node has userInput=true - WAITING for user input before proceeding to:',
                      result.nextNodeId
                    )
                    // Don't auto-progress - wait for user input
                  } else {
                    console.log(
                      '▶️ [FRONTEND] Entry node has userInput=false - AUTO-PROGRESSING to next node:',
                      result.nextNodeId,
                      '(type:',
                      result.nextNodeType,
                      'interactive:',
                      result.nextIsInteractive,
                      ')'
                    )
                    await runExecuteFlow('', result.nextNodeId)
                  }
                } else if (flowSession?.currentNodeId) {
                  console.log(
                    '▶️ [FLOW] Entry has no outgoing connection; continuing from flow start node:',
                    flowSession.currentNodeId
                  )
                  await runExecuteFlow('', flowSession.currentNodeId)
                }
              }

              // Reset execution flag but keep executedForSession
              entryExecutionRef.current.isExecuting = false
              setIsExecutingEntry(false)
            } catch (triggerError) {
              console.log(
                'No entry node found for session, will trigger on first message'
              )
              // Reset execution flags on error
              entryExecutionRef.current.isExecuting = false
              entryExecutionRef.current.executedForSession = null // Reset on error
              setIsExecutingEntry(false)
            }
          } else {
            console.log(
              '🔄 [FLOW] Skipping entry node trigger - already executed or in progress'
            )
          }
        } else if (initPayload && initPayload.success && !initPayload.data) {
          console.log(
            '🎯 [FLOW] No default flow - checking if flows are available'
          )

          // When there's no default flow but other flows might be available for LLM routing
          // Set hasDefaultFlow to true to show "Flow Available" but keep flowSession null
          console.log(
            '✅ [FLOW] No default flow set - flows available for LLM routing'
          )
          setHasDefaultFlow(true) // Show "Flow Available" indicator
          setIsFlowActive(false)
          setFlowSession(null)
          setFlowError(null)
        } else {
          // No default flow found - check if there are any active flows
          console.log('🚫 [FLOW] No default flow found for default site')

          // For now, just set hasDefaultFlow to false and let the chat system handle LLM routing
          // when user sends their first message
          console.log(
            '⏳ [FLOW] No default flow - will wait for user input to trigger LLM routing'
          )
          setHasDefaultFlow(false)
          // Don't set isFlowActive yet - wait for user input
        }

        // Mark initialization as complete
        initializationRef.current.isInitializing = false
        initializationRef.current.initializedForSession = sessionId
        console.log(
          '✅ [FLOW] Initialization completed for session:',
          sessionId
        )
      } catch (error) {
        console.error('Failed to initialize flow:', error)
        setFlowError('Failed to initialize flow')
        setHasDefaultFlow(false)

        // Mark initialization as complete even on error
        initializationRef.current.isInitializing = false
        console.log(
          '❌ [FLOW] Initialization failed but marked as complete for session:',
          sessionId
        )
      }
    },
    [
      buildDisplayMessage,
      checkAvailableFlows,
      cleanupStaleFlowSessions,
      emitFlowMessage,
      flowOverrideId,
      flowOverrideSlug,
      flowSession,
      hasExecutedEntry,
      isExecutingEntry,
      isFlowActive,
      isFlowCompleting,
      isHandedOver,
      isProcessingFlow,
      lastCompletionTime,
      onOpenChat,
      runExecuteFlow,
      isUserRegistered,
      resolvedSiteId,
      sessionId,
      setFlowError,
      setFlowSession,
      setHasDefaultFlow,
      setIsFlowActive,
      triggerEntryNodeForExistingSession,
    ]
  )

  // Execute flow step with improved error handling
  const executeFlow = useCallback(
    async (
      userInput?: string,
      nodeId?: string,
      displayText?: string
    ): Promise<FlowExecutionResult | null> => {
      if (!sessionId) {
        return null
      }

      // Hard early skip: if we've positively determined there is NO default flow configured
      // (hasDefaultFlow === false) and we also have no active session, avoid any execute attempts.
      // This prevents unnecessary /flows/execute calls that yield "No active flow session".
      // Preserve LLM routing ability when there is NO default flow but the user has provided input.
      // Only skip when there is no default flow, no session, AND no user input (system/auto calls).
      if (hasDefaultFlow === false && !flowSession && !userInput) {
        console.log(
          '[FLOW][NO_DEFAULT] Skipping auto execute (no default flow, no session, no user input)'
        )
        return null
      }

      // LLM routing should only trigger when:
      // 1. No flow session exists (!flowSession)
      // 2. User provided input (userInput)
      // 3. No flow is currently active (!isFlowActive)
      // We intentionally do NOT require hasDefaultFlow here, so LLM routing works
      // even when there is no default flow configured.
      if (
        !flowSession &&
        userInput &&
        !isFlowActive &&
        !flowExitModeRef.current
      ) {
        console.log(
          '🤖 [FLOW] No default flow active, attempting LLM routing with user input:',
          userInput
        )
        try {
          // Try to initialize a flow session with user input for LLM routing
          const initResult = await api.flows.initialize({
            sessionId,
            triggerContext: 'flow_start',
            userMessage: userInput,
          })

          console.log(
            '🤖 [FLOW] LLM routing initialization result:',
            initResult
          )

          if (initResult.success && initResult.data) {
            // Flow was selected and initialized
            console.log(
              '✅ [FLOW] LLM routing successful, setting flow session:',
              {
                flowId: initResult.data.flowId,
                currentNodeId: initResult.data.currentNodeId,
                message: initResult.data.message,
              }
            )

            setFlowSession(normalizeFlowSession(initResult.data))
            setIsFlowActive(true)

            // After LLM routing, execute the flow with the new session data
            console.log(
              '🎬 [FLOW] LLM routing successful, now executing flow with new session...'
            )

            // Use the new flow session data directly (don't wait for state update)
            const newFlowSession = initResult.data

            try {
              let nextNodeId: string | undefined
              if (typeof newFlowSession.currentNodeId === 'string') {
                nextNodeId = newFlowSession.currentNodeId
              } else if (newFlowSession.currentNodeId != null) {
                nextNodeId = String(newFlowSession.currentNodeId)
              }

              const flowResult = await api.flows.execute({
                sessionId,
                nodeId: nextNodeId ?? '',
                userInput,
                platform: 'web',
              })

              console.log(
                '🎬 [FLOW] Flow execution after LLM routing:',
                flowResult
              )

              if (flowResult.success && flowResult.data) {
                // Normalize to the same shape as normal execute path
                let result: any =
                  (flowResult as any).data?.data || (flowResult as any).data

                // Update session if provided (LLM path)
                if ((flowResult as any).data?.flowSession) {
                  setFlowSession(
                    normalizeFlowSession((flowResult as any).data.flowSession)
                  )
                }

                // Deliver the first message immediately
                if (
                  result &&
                  hasDisplayContent(
                    buildDisplayMessage(result),
                    result.attachments
                  )
                ) {
                  emitFlowMessage(
                    buildDisplayMessage(result) ?? '',
                    result.attachments,
                    result.paymentRequest
                  )
                }
                if (result?.menuOptions && result.menuOptions.length > 0) {
                  onMenuOptions?.(result.message || '', result.menuOptions)
                }

                // Auto-progress just like the normal path
                {
                  let safety = 0
                  while (canAutoProgress(result) && safety < 5) {
                    try {
                      const followResp = await api.flows.execute({
                        sessionId,
                        userInput: '',
                        nodeId: result.nextNodeId,
                        platform: 'web',
                      })
                      if (!followResp.success) break
                      const follow: any =
                        followResp.data?.data || followResp.data

                      // Update session if provided
                      if (followResp.data?.flowSession) {
                        setFlowSession(
                          normalizeFlowSession(followResp.data.flowSession)
                        )
                      } else if (follow.nextNodeId) {
                        setFlowSession(prev =>
                          prev
                            ? { ...prev, currentNodeId: follow.nextNodeId }
                            : prev
                        )
                      }

                      // Deliver any message from the auto-progressed node
                      const followMessage = combineMessageWithLink(
                        follow.message,
                        (follow as any)?.openUrl
                      )
                      if (
                        hasDisplayContent(followMessage, follow.attachments)
                      ) {
                        emitFlowMessage(
                          followMessage ?? '',
                          follow.attachments,
                          follow.paymentRequest
                        )
                      }
                      if (follow.menuOptions && follow.menuOptions.length > 0) {
                        onMenuOptions?.(
                          follow.message || '',
                          follow.menuOptions
                        )
                      }

                      // Move forward
                      result = follow
                      safety += 1

                      // Exit loop if next node requires user input
                      if (!canAutoProgress(result)) break
                    } catch (apErr) {
                      console.warn(
                        '⚠️ [FLOW] (LLM) Auto-progression failed',
                        apErr
                      )
                      break
                    }
                  }
                }

                console.log(
                  '🎯 [FLOW] Returning LLM routing + execution result:',
                  result
                )
                return result
              } else {
                console.log('❌ [FLOW] Flow execution after LLM routing failed')
                const nextNodeIdAfterLLM =
                  typeof newFlowSession.currentNodeId === 'string'
                    ? newFlowSession.currentNodeId
                    : newFlowSession.currentNodeId != null
                      ? String(newFlowSession.currentNodeId)
                      : undefined

                return {
                  success: true,
                  message: 'Flow started',
                  flowComplete: false,
                  nextNodeId: nextNodeIdAfterLLM,
                }
              }
            } catch (error) {
              console.error(
                '❌ [FLOW] Error executing flow after LLM routing:',
                error
              )

              const nextNodeIdAfterLLM =
                typeof newFlowSession.currentNodeId === 'string'
                  ? newFlowSession.currentNodeId
                  : newFlowSession.currentNodeId != null
                    ? String(newFlowSession.currentNodeId)
                    : undefined

              return {
                success: true,
                message: 'Flow started',
                flowComplete: false,
                nextNodeId: nextNodeIdAfterLLM,
              }
            }
          } else {
            console.log('❌ [FLOW] LLM routing failed, falling back to AI chat')
            return null
          }
        } catch (error) {
          console.error('❌ [FLOW] Error during LLM routing:', error)
          return null
        }
      }

      // Allow executing a specific node even if local flowSession state isn't set yet.
      // This happens right after Entry trigger, before setFlowSession state applies.
      if (!flowSession && !nodeId) {
        console.log('🚫 [FLOW] No flow session and no target node provided')
        return null
      }

      // If already processing, skip this call to avoid duplicate execution
      if (isProcessingFlowRef.current) {
        console.warn('Flow is still processing, skipping duplicate request')
        return null
      }

      // Defer any follow-up action (restart/redirect) until after processing flag is cleared
      let postCompletionTask: (() => Promise<void>) | null = null
      try {
        setIsProcessingFlow(true)
        isProcessingFlowRef.current = true
        setFlowError(null)

        // Track user's last message for NLP redirect
        if (userInput) {
          setLastUserMessage(userInput)
          setUserMessageHistory(prev => [...prev, userInput])
        }

        const resolvedNodeId: string =
          nodeId ?? flowSession?.currentNodeId ?? ''
        console.log('🎯 [EXECUTE FLOW] About to execute with:', {
          userInput,
          providedNodeId: nodeId,
          flowSessionCurrentNodeId: flowSession?.currentNodeId,
          resolvedNodeId,
          sessionId,
        })

        const response = await api.flows.execute({
          sessionId,
          userInput,
          displayText,
          nodeId: resolvedNodeId,
          platform: 'web',
        })

        if (response.success) {
          // Fix: The actual flow execution result is in response.data.data, not response.data
          const responseData: any = response.data ?? {}
          let result: FlowExecutionResult = (responseData.data ??
            responseData) as FlowExecutionResult

          console.log(
            '📡 [FRONTEND] API status:',
            response.success ? 'success' : 'failed'
          )
          if (!response.success) {
            console.error('❌ [FRONTEND] API error:', {
              success: response.success,
              error: response.error,
              message: response.message,
            })
          }
          console.log('📡 [FRONTEND] Extracted flow execution result:', {
            success: result.success,
            message: result.message,
            flowComplete: result.flowComplete,
            useNlpRedirect: result.useNlpRedirect,
            redirectFlowName: result.redirectFlowName,
            redirectFlowId: result.redirectFlowId,
            shouldRestart: result.shouldRestart,
            hasFormNode: !!result.formNode,
            hasMenuOptions: !!result.menuOptions,
            openChat: result.openChat,
            nextNodeId: result.nextNodeId,
            userInputFlag: result.userInput,
            hasBotAssignment: !!result.botAssignment,
            hasInitialBotMessage: !!result.botAssignmentInitialMessage,
            hasLegacyBot:
              (result as any)?.requiresBotAssignment ||
              !!(result as any)?.botAssignmentConfig,
            userMessage: userInput,
            currentNodeId: nodeId,
          })

          // Legacy compatibility: older servers return requiresBotAssignment/botAssignmentConfig
          if (
            !result.botAssignment &&
            ((result as any)?.requiresBotAssignment ||
              (result as any)?.botAssignmentConfig)
          ) {
            const legacyConfig = (result as any).botAssignmentConfig ?? {}
            const rawBotId =
              legacyConfig.botId ??
              legacyConfig.id ??
              legacyConfig.bot_id ??
              null

            const parsedBotId =
              typeof rawBotId === 'number'
                ? rawBotId
                : rawBotId !== null
                  ? Number(rawBotId)
                  : NaN

            const rawLegacyTeamId =
              legacyConfig.teamId ??
              legacyConfig.team_id ??
              legacyConfig.teamID ??
              null
            const parsedTeamId =
              typeof rawLegacyTeamId === 'number'
                ? rawLegacyTeamId
                : rawLegacyTeamId !== null
                  ? Number.parseInt(String(rawLegacyTeamId), 10)
                  : NaN

            if (Number.isFinite(parsedBotId)) {
              result.botAssignment = {
                botId: parsedBotId,
                transferType:
                  legacyConfig.transferType === 'context' ? 'context' : 'full',
                ...(Number.isFinite(parsedTeamId) && parsedTeamId > 0
                  ? { teamId: parsedTeamId }
                  : {}),
              }
              console.log(
                '🔁 [FRONTEND] Normalized legacy bot assignment payload:',
                result.botAssignment
              )
            }
          }

          // Auto-recover if backend reports missing flow session for this chat session
          if (
            result &&
            result.success === false &&
            typeof result.error === 'string' &&
            result.error.toLowerCase().includes('no active flow session')
          ) {
            console.warn(
              '⚠️ [FRONTEND] No active flow session on server. Re-initializing and retrying once...'
            )
            try {
              await initializeFlow(true)
              const retry = await api.flows.execute({
                sessionId,
                userInput,
                displayText,
                nodeId: nodeId ?? flowSession?.currentNodeId ?? '',
                platform: 'web',
              })
              if (retry.success) {
                // Replace response/result with retry and continue normal processing
                // Cast to any to satisfy typing for response.data (JsonObject | undefined)
                response.data = (retry.data?.data ?? retry.data ?? retry) as any
                const r: any = retry.data?.data || retry.data || retry
                result = r
              } else {
                console.warn(
                  '⚠️ [FRONTEND] Retry execute failed, falling through'
                )
              }
            } catch (retryErr) {
              console.error(
                '❌ [FRONTEND] Retry after re-init failed:',
                retryErr
              )
            }
          }

          console.log(
            '🔍 [FRONTEND] onFlowMessage callback:',
            typeof onFlowMessage,
            onFlowMessage
          )

          // Update flow session if provided (flowSession is in the raw response, not the result)
          if (responseData.flowSession) {
            setFlowSession(normalizeFlowSession(responseData.flowSession))
          } else if (result.nextNodeId && flowSession) {
            // If no flowSession in response but we have nextNodeId, update the current session
            const updatedSession = {
              ...flowSession,
              currentNodeId: result.nextNodeId,
            }
            setFlowSession(updatedSession)
            // Also update the local flowSession reference for immediate use in auto-progression
            flowSession.currentNodeId = result.nextNodeId
          }

          // Handle special actions
          if (result.flowComplete) {
            console.log('🏁 [FLOW] Flow completed, cleaning up...', {
              shouldRestart: result.shouldRestart,
              redirectFlowId: result.redirectFlowId,
              restartDelay: result.restartDelay,
            })
            setIsFlowCompleting(true) // Prevent reinitialization during cleanup

            // Handle different completion actions
            if (result.shouldRestart) {
              console.log(
                '🔄 [FLOW] Flow will restart after delay:',
                result.restartDelay || 1000,
                'ms'
              )

              // Always show end message before restart if provided
              // Add a small delay to ensure the message is rendered before restart begins
              const showEndMessage = async () => {
                if (
                  hasDisplayContent(
                    buildDisplayMessage(result),
                    result.attachments
                  )
                ) {
                  console.log(
                    '📨 [FLOW] Showing end node message before restart:',
                    result.message
                  )
                  emitFlowMessage(
                    buildDisplayMessage(result) ?? '',
                    result.attachments,
                    result.paymentRequest
                  )
                  // Wait a bit to ensure message is rendered
                  await new Promise(resolve => setTimeout(resolve, 300))
                }
              }

              // Helper to run a full restart cycle and chain if another restart is requested
              const runRestartCycle = async (delay?: number) => {
                // Show end message first and wait for it to render
                await showEndMessage()

                // Wait for the specified delay before restarting
                if (delay) {
                  await new Promise(resolve => setTimeout(resolve, delay))
                }

                console.log('🔄 [FLOW] Restarting flow now...')
                console.log('🔄 [FLOW] Resetting ALL flow state for restart')
                // Reset ALL flow state for restart
                setIsFlowActive(false)
                setIsFlowCompleting(false)
                setIsExecutingEntry(false)
                setHasExecutedEntry(false)
                entryExecutionRef.current = {
                  isExecuting: false,
                  executedForSession: null,
                }
                console.log(
                  '🔄 [FLOW] All flow state reset, waiting before re-initializing...'
                )

                // Wait a bit for state to update, then reset and process first Entry directly
                await new Promise(resolve => setTimeout(resolve, 100))
                console.log('🔄 [FLOW] Resetting flow session on server...')
                console.log('🔄 [FLOW] Current flowSession:', flowSession)
                console.log('🔄 [FLOW] Current sessionId (chat):', sessionId)
                try {
                  // IMPORTANT: We need to pass the FLOW SESSION ID (flowSession.id) to the reset endpoint
                  // The backend will then use flowSession.sessionId (chat session ID) for message storage
                  const flowSessionIdToReset = flowSession?.id
                  if (!flowSessionIdToReset) {
                    console.error(
                      '❌ [FLOW] No flow session ID available for reset'
                    )
                    return
                  }
                  console.log(
                    '🔄 [FLOW] Resetting flow session ID:',
                    flowSessionIdToReset
                  )
                  const resetResp = await api.flows.reset({
                    sessionId: flowSessionIdToReset,
                  })
                  console.log(
                    '✅ [FLOW] Flow session reset on server (got first node result)'
                  )

                  if (!resetResp?.success) {
                    console.warn(
                      '⚠️ [FLOW] Reset API returned unsuccessful response'
                    )
                    return
                  }

                  // Normalize result shape from reset
                  const resetPayload: any = resetResp as any
                  let first: any = resetPayload.data?.data || resetPayload.data

                  // Mark flow as active again
                  setIsFlowActive(true)

                  // Update session if provided
                  if (resetPayload.data?.flowSession) {
                    setFlowSession(
                      normalizeFlowSession(resetPayload.data.flowSession)
                    )
                  } else if (first?.nextNodeId) {
                    setFlowSession(prev =>
                      prev ? { ...prev, currentNodeId: first.nextNodeId } : prev
                    )
                  }

                  // Deliver Entry message immediately (server already persisted it).
                  // ChatCore will de-duplicate with history if it loads later.
                  const entryMessage = combineMessageWithLink(
                    first?.message,
                    first?.openUrl
                  )
                  if (
                    first &&
                    hasDisplayContent(entryMessage, first.attachments)
                  ) {
                    console.log(
                      '📨 [FLOW] Delivering entry node message after restart:',
                      entryMessage
                    )
                    emitFlowMessage(
                      entryMessage ?? '',
                      first.attachments,
                      first?.paymentRequest
                    )
                  }
                  if (
                    first?.botAssignmentInitialMessage &&
                    first.botAssignmentInitialMessage.trim() !== ''
                  ) {
                    emitFlowMessage(
                      first.botAssignmentInitialMessage ?? '',
                      undefined
                    )
                  }
                  if (first?.openChat) {
                    onOpenChat?.()
                  }
                  if (first?.menuOptions && first.menuOptions.length > 0) {
                    onMenuOptions?.(
                      entryMessage || first.message || '',
                      first.menuOptions
                    )
                  }

                  // Auto-progress from the returned node according to server flags
                  {
                    let safety = 0
                    while (canAutoProgress(first) && safety < 5) {
                      try {
                        const followResp = await api.flows.execute({
                          sessionId,
                          userInput: '',
                          nodeId: first.nextNodeId,
                          platform: 'web',
                        })
                        if (!followResp.success) break
                        const follow: any =
                          followResp.data?.data || followResp.data

                        // Update session
                        if (followResp.data?.flowSession) {
                          setFlowSession(
                            normalizeFlowSession(followResp.data.flowSession)
                          )
                        } else if (follow.nextNodeId) {
                          setFlowSession(prev =>
                            prev
                              ? {
                                  ...prev,
                                  currentNodeId: follow.nextNodeId,
                                }
                              : prev
                          )
                        }

                        // Deliver any message
                        const followMessage = combineMessageWithLink(
                          follow.message,
                          (follow as any)?.openUrl
                        )
                        if (
                          hasDisplayContent(followMessage, follow.attachments)
                        ) {
                          emitFlowMessage(
                            followMessage ?? '',
                            follow.attachments,
                            follow.paymentRequest
                          )
                        }
                        if (
                          follow.menuOptions &&
                          follow.menuOptions.length > 0
                        ) {
                          onMenuOptions?.(
                            follow.message || '',
                            follow.menuOptions
                          )
                        }

                        first = follow
                        safety += 1

                        if (!canAutoProgress(first)) break
                      } catch (apErr) {
                        console.warn(
                          '⚠️ [FLOW] Auto-progression after reset failed',
                          apErr
                        )
                        break
                      }
                    }
                  }

                  // Chain next restart if this cycle completed with restart
                  if (first?.flowComplete && first?.shouldRestart) {
                    console.log(
                      '🔄 [FLOW] Another restart requested; chaining next cycle'
                    )
                    const nextDelay = first.restartDelay || 1000
                    runRestartCycle(nextDelay)
                  }
                } catch (error) {
                  console.error('❌ [FLOW] Error during flow restart:', error)
                }
              }

              // Defer the first restart cycle until after isProcessingFlow is cleared
              postCompletionTask = async () => {
                await runRestartCycle(result.restartDelay || 1000)
              }
            } else if (
              result.redirectFlowId ||
              result.redirectFlowName ||
              result.useNlpRedirect
            ) {
              if (result.redirectFlowId) {
                console.log(
                  '🔀 [FLOW] Flow will redirect to flow ID:',
                  result.redirectFlowId
                )
              } else if (result.redirectFlowName) {
                console.log(
                  '🔀 [FLOW] Flow will redirect to flow name:',
                  result.redirectFlowName
                )
              } else if (result.useNlpRedirect) {
                console.log(
                  '🔍 [FLOW] Flow will use NLP to determine redirect flow based on user input'
                )
              }

              // Defer redirect until after isProcessingFlow is cleared
              console.log('🔀 [FLOW] Setting up deferred redirect task')
              postCompletionTask = async () => {
                console.log('🔀 [FLOW] Deferred redirect task starting...')
                try {
                  // Reset current flow state
                  console.log('🔀 [FLOW] Resetting flow state for redirect')
                  setIsFlowActive(false)
                  setFlowSession(null)
                  setIsFlowCompleting(false)

                  // Reset entry execution tracking for new flow
                  entryExecutionRef.current = {
                    isExecuting: false,
                    executedForSession: null,
                  }
                  setHasExecutedEntry(false)
                  setIsExecutingEntry(false)

                  let selectedFlowName = ''

                  // If using NLP redirect, call LLM API to get flow selection
                  if (result.useNlpRedirect) {
                    try {
                      const llmResponse = await api.flows.routeWithLLM({
                        sessionId: sessionId, // Pass session ID so backend can get stored user message
                        systemMessage:
                          'The user has completed a conversation flow and needs to be redirected to the most appropriate next flow based on their last message.',
                      })

                      if (llmResponse.success) {
                        selectedFlowName = llmResponse.data.selectedFlow.name
                      } else {
                        console.error(
                          '❌ [FLOW] LLM API error:',
                          llmResponse.error
                        )
                        throw new Error(`LLM API error: ${llmResponse.error}`)
                      }
                    } catch (llmError) {
                      console.error('❌ [FLOW] LLM API call failed:', llmError)
                      throw llmError
                    }
                  } else if (result.redirectFlowName) {
                    selectedFlowName = result.redirectFlowName
                  } else {
                    selectedFlowName = `ID:${result.redirectFlowId}`
                  }

                  // Initialize new flow session with the selected flow name
                  const initResponse = await api.flows.initialize({
                    sessionId,
                    triggerContext: 'flow_redirect',
                    userMessage: selectedFlowName,
                  })

                  if (initResponse.success && initResponse.data) {
                    // Prevent the initialization useEffect from triggering
                    // a duplicate chat_initialize call that races with our
                    // flow_start trigger and consumes the resumeNodeId
                    entryExecutionRef.current.isExecuting = true

                    setFlowSession(normalizeFlowSession(initResponse.data))
                    setIsFlowActive(true)

                    // Trigger entry node for new flow if it exists
                    try {
                      const triggerResponse = await api.flows.trigger({
                        sessionId: sessionId, // Use the same chat session ID; backend will route to the new flow session
                        triggerContext: 'flow_start', // Use flow_start to use existing session instead of creating new one
                      })

                      if (triggerResponse.success && triggerResponse.data) {
                        const triggerResult = triggerResponse.data

                        // Mark entry as executed only on success
                        entryExecutionRef.current.executedForSession = sessionId
                        setHasExecutedEntry(true)

                        // Display entry message for redirected flow
                        const redirectMessage = combineMessageWithLink(
                          triggerResult.message as string | undefined,
                          (triggerResult as any)?.openUrl
                        )
                        if (
                          hasDisplayContent(
                            redirectMessage,
                            triggerResult.attachments as any
                          )
                        ) {
                          console.log(
                            '[FLOW] Showing entry node message after redirect:',
                            redirectMessage
                          )
                          emitFlowMessage(
                            redirectMessage ?? '',
                            triggerResult.attachments as any,
                            (triggerResult as any)?.paymentRequest
                          )
                        }

                        // Handle auto-open chat from entry nodes
                        if (triggerResult.openChat) {
                          console.log(
                            '💬 [FLOW] Entry node requested auto-open chat (flow_start)'
                          )
                          onOpenChat?.()
                        }

                        // Update session with new current node
                        if (triggerResult.nextNodeId) {
                          setFlowSession(prev =>
                            prev
                              ? {
                                  ...prev,
                                  currentNodeId:
                                    triggerResult.nextNodeId as string,
                                }
                              : prev
                          )

                          // Check if Entry node wants to wait for user input
                          if ((triggerResult as any).userInput === true) {
                            console.log(
                              '⏸️ [FLOW] Entry node (redirect) has autoProgress OFF - waiting for user input before proceeding to:',
                              triggerResult.nextNodeId
                            )
                            // Don't auto-progress - wait for user input
                          } else {
                            // Continue from Entry (after redirect) regardless of interactivity; executeFlow will handle UI
                            console.log(
                              '▶️ [FLOW] Continuing from Entry (redirect) to next node:',
                              triggerResult.nextNodeId,
                              '(type:',
                              triggerResult.nextNodeType,
                              'interactive:',
                              triggerResult.nextIsInteractive,
                              ')'
                            )
                            await executeFlow(
                              '',
                              triggerResult.nextNodeId as string
                            )
                          }
                        }
                      }
                    } catch (triggerError) {
                      console.log('No entry node found in redirected flow')
                    } finally {
                      entryExecutionRef.current.isExecuting = false
                    }
                  } else {
                    console.error('Failed to redirect to new flow')
                    onFlowComplete?.()
                  }
                } catch (error) {
                  console.error('Error during flow redirect:', error)
                  onFlowComplete?.()
                }
              }
            } else {
              // Exit action - show message first, then clean up flow and STOP
              console.log('🚪 [FLOW] Flow exiting - showing exit message first')

              // Mark that we intentionally exited; prevent auto (re)initialization and LLM routing for this session
              flowExitModeRef.current = true

              // Show the exit message immediately
              if (
                hasDisplayContent(
                  buildDisplayMessage(result),
                  result.attachments
                )
              ) {
                console.log(
                  '🚪 [FLOW] Sending exit message to ChatCore:',
                  result.message
                )
                emitFlowMessage(
                  buildDisplayMessage(result) ?? '',
                  result.attachments,
                  result.paymentRequest
                )
              }

              setLastCompletionTime(Date.now()) // Set completion time for cooldown

              // Give time for the exit message to be displayed before cleanup
              setTimeout(() => {
                console.log(
                  '🚪 [FLOW] Exit delay completed - cleaning up flow now'
                )
                setIsFlowActive(false)
                setFlowSession(null)
                setIsFlowCompleting(false)
                onFlowComplete?.()
              }, 1500) // Increased delay to ensure message is visible
              // Return early to prevent any further processing
              return result
            }
          }

          if (result.botAssignment) {
            setIsFlowActive(false)
            console.log('🤖 [FLOW] Bot assignment detected:', {
              botId: result.botAssignment.botId,
              transferType: result.botAssignment.transferType,
              teamId: result.botAssignment.teamId,
              requiresBotResponse: result.requiresBotResponse,
              hasCallback: !!onBotAssignment,
            })
            onBotAssignment?.(
              result.botAssignment.botId,
              result.botAssignment.transferType,
              typeof result.botAssignment.teamId === 'number' &&
                Number.isFinite(result.botAssignment.teamId)
                ? result.botAssignment.teamId
                : null,
              result.requiresBotResponse // Pass requiresBotResponse flag
            )
          }

          if (result.agentHandover) {
            setIsFlowActive(false)
            const rawTeamId = result.agentHandover.teamId
            const normalizedTeamId =
              typeof rawTeamId === 'number'
                ? rawTeamId
                : typeof rawTeamId === 'string' && rawTeamId.trim().length > 0
                  ? Number.parseInt(rawTeamId.trim(), 10)
                  : null
            onAgentHandover?.({
              ...result.agentHandover,
              teamId:
                normalizedTeamId !== null && Number.isFinite(normalizedTeamId)
                  ? normalizedTeamId
                  : null,
              teamName:
                typeof result.agentHandover.teamName === 'string'
                  ? result.agentHandover.teamName
                  : null,
            })
          }

          // Handle auto-open chat from entry nodes
          if (result.openChat) {
            console.log('💬 [FLOW] Entry node requested auto-open chat')
            onOpenChat?.()
          }

          // Handle message display - this is the main place where flow messages are sent to ChatCore
          if (
            hasDisplayContent(buildDisplayMessage(result), result.attachments)
          ) {
            console.log(
              '📢 [FLOW] Sending flow message to ChatCore:',
              result.message
            )
            emitFlowMessage(
              buildDisplayMessage(result) ?? '',
              result.attachments,
              result.paymentRequest
            )
          }
          if (
            result.botAssignmentInitialMessage &&
            result.botAssignmentInitialMessage.trim() !== ''
          ) {
            emitFlowMessage(result.botAssignmentInitialMessage ?? '', undefined)
          }
          if (result.menuOptions && result.menuOptions.length > 0) {
            onMenuOptions?.(result.message || '', result.menuOptions)
          }

          // Handle form nodes - emit them via onFlowMessage so ChatCore can render
          if (result.formNode) {
            console.log(
              '📋 [FLOW] Form node detected, emitting to ChatCore:',
              result.formNode
            )
            onFlowMessage?.({
              type: 'form',
              content: result.message || '',
              attachments: result.attachments,
              formNode: result.formNode,
            })
          }

          // Determine if we should auto-progress to the next node.
          const shouldAutoProgress = canAutoProgress(result)

          if (shouldAutoProgress && result.nextNodeId) {
            const nextNodeId = result.nextNodeId
            const nextNodeType = result.nextNodeType

            if (flowSession) {
              setFlowSession(prev =>
                prev ? { ...prev, currentNodeId: nextNodeId } : prev
              )
              flowSession.currentNodeId = nextNodeId
            }

            if (autoProgressTimeoutRef.current) {
              clearTimeout(autoProgressTimeoutRef.current)
            }

            autoProgressTimeoutRef.current = setTimeout(() => {
              console.log(
                '▶️ [FLOW] Scheduled auto-progress to next node:',
                nextNodeId,
                '(type:',
                nextNodeType,
                ')'
              )
              runExecuteFlow('', nextNodeId).catch(apErr => {
                console.warn(
                  '⚠️ [FLOW] Auto-progression failed, falling back to wait for input',
                  apErr
                )
              })
            }, 150)
          }

          // Handle completion after auto-progression (restart/redirect/exit)
          if (result.flowComplete && !postCompletionTask) {
            setIsFlowCompleting(true)

            if (result.shouldRestart) {
              // Schedule a restart cycle similar to the primary completion path
              postCompletionTask = async () => {
                try {
                  // Optionally show end message
                  if (
                    hasDisplayContent(
                      buildDisplayMessage(result),
                      result.attachments
                    )
                  ) {
                    emitFlowMessage(
                      buildDisplayMessage(result) ?? '',
                      result.attachments,
                      result.paymentRequest
                    )
                    await new Promise(resolve => setTimeout(resolve, 300))
                  }

                  const delay = result.restartDelay || 1000
                  if (delay) {
                    await new Promise(resolve => setTimeout(resolve, delay))
                  }

                  // Reset ALL flow state for restart
                  setIsFlowActive(false)
                  setIsFlowCompleting(false)
                  setIsExecutingEntry(false)
                  setHasExecutedEntry(false)
                  entryExecutionRef.current = {
                    isExecuting: false,
                    executedForSession: null,
                  }

                  // Give state a moment to settle
                  await new Promise(resolve => setTimeout(resolve, 100))

                  // Reset server-side flow session and auto-progress
                  const flowSessionIdToReset = flowSession?.id
                  if (!flowSessionIdToReset) return

                  const resetResp = await api.flows.reset({
                    sessionId: flowSessionIdToReset,
                  })
                  if (!resetResp?.success) return

                  const resetPayload: any = resetResp as any
                  const first: any =
                    resetPayload.data?.data || resetPayload.data

                  setIsFlowActive(true)

                  if (resetPayload.data?.flowSession) {
                    setFlowSession(resetPayload.data.flowSession)
                  } else if (first?.nextNodeId) {
                    setFlowSession(prev =>
                      prev ? { ...prev, currentNodeId: first.nextNodeId } : prev
                    )
                  }

                  const firstMessage = combineMessageWithLink(
                    first?.message,
                    first?.openUrl
                  )
                  if (
                    first &&
                    hasDisplayContent(firstMessage, first.attachments)
                  ) {
                    emitFlowMessage(
                      firstMessage ?? '',
                      first.attachments,
                      first?.paymentRequest
                    )
                  }
                  if (
                    first?.botAssignmentInitialMessage &&
                    first.botAssignmentInitialMessage.trim() !== ''
                  ) {
                    emitFlowMessage(
                      first.botAssignmentInitialMessage ?? '',
                      undefined
                    )
                  }
                  if (first?.openChat) onOpenChat?.()
                  if (first?.menuOptions && first.menuOptions.length > 0) {
                    onMenuOptions?.(
                      firstMessage || first.message || '',
                      first.menuOptions
                    )
                  }

                  if (first?.nextNodeId && (first as any).userInput === false) {
                    await executeFlow('', first.nextNodeId)
                  }
                } catch (e) {
                  console.error(
                    '❌ [FLOW] Error during auto-progression restart after completion:',
                    e
                  )
                }
              }
            } else if (
              result.redirectFlowId ||
              result.redirectFlowName ||
              result.useNlpRedirect
            ) {
              console.log('🔀 [FLOW] Setting up deferred redirect task (AP)')
              postCompletionTask = async () => {
                console.log('� [FLOW] Deferred redirect task starting (AP)...')
                try {
                  // Reset state prior to redirect
                  setIsFlowActive(false)
                  setFlowSession(null)
                  setIsFlowCompleting(false)
                  setIsExecutingEntry(false)
                  setHasExecutedEntry(false)
                  entryExecutionRef.current = {
                    isExecuting: false,
                    executedForSession: null,
                  }

                  let selectedFlowName = ''
                  if (result.useNlpRedirect) {
                    // Fallback: if NLP not resolved here, rely on backend initialize to resolve by name when available
                  } else if (result.redirectFlowName) {
                    selectedFlowName = result.redirectFlowName
                  } else {
                    selectedFlowName = `ID:${result.redirectFlowId}`
                  }

                  const initResponse = await api.flows.initialize({
                    sessionId,
                    triggerContext: 'flow_redirect',
                    userMessage: selectedFlowName,
                  })

                  if (initResponse.success && initResponse.data) {
                    // Prevent the initialization useEffect from triggering
                    // a duplicate chat_initialize call that races with our
                    // flow_start trigger and consumes the resumeNodeId
                    entryExecutionRef.current.isExecuting = true

                    setFlowSession(normalizeFlowSession(initResponse.data))
                    setIsFlowActive(true)

                    try {
                      const triggerResponse = await api.flows.trigger({
                        sessionId: sessionId,
                        triggerContext: 'flow_start',
                      })

                      if (triggerResponse.success && triggerResponse.data) {
                        const triggerResult = triggerResponse.data

                        // Mark entry as executed only on success
                        entryExecutionRef.current.executedForSession = sessionId
                        setHasExecutedEntry(true)

                        if (triggerResult.openChat) onOpenChat?.()
                        const triggerMessage = combineMessageWithLink(
                          triggerResult.message,
                          triggerResult.openUrl
                        )
                        if (
                          hasDisplayContent(
                            triggerMessage,
                            triggerResult.attachments
                          )
                        ) {
                          emitFlowMessage(
                            triggerMessage ?? '',
                            triggerResult.attachments,
                            triggerResult?.paymentRequest
                          )
                        }
                        if (
                          triggerResult.menuOptions &&
                          triggerResult.menuOptions.length > 0
                        ) {
                          onMenuOptions?.(
                            triggerResult.message || '',
                            triggerResult.menuOptions
                          )
                        }
                        if (
                          triggerResult.nextNodeId &&
                          !triggerResult.userInput
                        ) {
                          await executeFlow(
                            '',
                            triggerResult.nextNodeId as string
                          )
                        }
                      }
                    } finally {
                      entryExecutionRef.current.isExecuting = false
                    }
                  }
                } catch (error) {
                  console.error('Error during flow redirect (AP):', error)
                  onFlowComplete?.()
                }
              }
            } else {
              onFlowComplete?.()
            }
          }

          // Debug: Log the final result after possible auto-progression
          console.log('🔍 [FLOW] Final execution result:', {
            nextNodeId: result.nextNodeId,
            flowComplete: result.flowComplete,
            hasMenuOptions: !!result.menuOptions,
            hasUserInput: !!result.userInput,
            message: result.message,
          })

          // If there's a deferred task, execute it immediately before returning
          if (postCompletionTask) {
            console.log(
              '🎬 [FLOW] Executing deferred task immediately before return'
            )
            const task = postCompletionTask
            postCompletionTask = null
            setIsProcessingFlow(false)
            // Execute the task without awaiting - let it run in the background
            task().catch((err: any) =>
              console.error('Error in deferred task:', err)
            )
            return result
          }

          return result
        } else {
          console.error('❌ [FLOW] Flow execution failed:', {
            success: response.success,
            error: response.error,
            message: response.message,
            status: 'failed',
          })
          setFlowError(response.error || 'Flow execution failed')
          return null
        }
      } catch (error) {
        console.error('Flow execution error:', error)
        setFlowError('Flow execution error')
        return null
      } finally {
        // Don't clear processing flag here - let it clear after the result is returned
        // This allows the caller to see the processing state before return
        // The flag will be cleared by a microtask after return
        isProcessingFlowRef.current = false
        Promise.resolve().then(() => setIsProcessingFlow(false))
      }
    },
    [
      buildDisplayMessage,
      sessionId,
      flowSession,
      hasDefaultFlow,
      initializeFlow,
      onBotAssignment,
      onAgentHandover,
      onFlowComplete,
      onFlowMessage,
      onMenuOptions,
      onOpenChat,
      isFlowActive,
      emitFlowMessage,
      runExecuteFlow,
    ]
  )

  useEffect(() => {
    executeFlowRef.current = executeFlow
  }, [executeFlow])

  // Reset flow session
  const resetFlow = useCallback(async () => {
    if (!sessionId) return

    try {
      setFlowError(null)

      const flowSessionIdToReset = flowSession?.sessionId ?? sessionId
      const resetResp = await api.flows.reset({
        sessionId: flowSessionIdToReset,
        flowId: flowOverrideId ?? undefined,
        flowSlug: flowOverrideSlug ?? undefined,
      })
      if (!resetResp?.success) {
        console.error('❌ [FLOW] Reset API error:', {
          success: resetResp?.success,
          error: resetResp?.error,
          message: resetResp?.message,
        })
        setFlowError(resetResp?.error || resetResp?.message || 'Reset failed')
        return
      }

      const resetPayload: any = resetResp as any
      let first: any = resetPayload.data?.data || resetPayload.data

      setIsFlowActive(true)
      setIsFlowCompleting(false)
      setIsExecutingEntry(false)
      setLastCompletionTime(0) // Clear completion time to allow reinitialization

      if (resetPayload.data?.flowSession) {
        setFlowSession(normalizeFlowSession(resetPayload.data.flowSession))
      } else if (first?.nextNodeId) {
        setFlowSession(prev =>
          prev ? { ...prev, currentNodeId: first.nextNodeId } : prev
        )
      }

      entryExecutionRef.current.isExecuting = false
      entryExecutionRef.current.executedForSession = sessionId
      setHasExecutedEntry(true)

      const entryMessage = combineMessageWithLink(
        first?.message,
        first?.openUrl
      )
      if (first && hasDisplayContent(entryMessage, first.attachments)) {
        emitFlowMessage(
          entryMessage ?? '',
          first.attachments,
          first?.paymentRequest
        )
      }
      if (
        first?.botAssignmentInitialMessage &&
        first.botAssignmentInitialMessage.trim() !== ''
      ) {
        emitFlowMessage(first.botAssignmentInitialMessage ?? '', undefined)
      }
      if (first?.openChat) {
        onOpenChat?.()
      }
      if (first?.menuOptions && first.menuOptions.length > 0) {
        onMenuOptions?.(entryMessage || first.message || '', first.menuOptions)
      }

      {
        let safety = 0
        while (canAutoProgress(first) && safety < 5) {
          try {
            const followResp = await api.flows.execute({
              sessionId,
              userInput: '',
              nodeId: first.nextNodeId,
              platform: 'web',
            })
            if (!followResp.success) break
            const follow: any = followResp.data?.data || followResp.data

            if (followResp.data?.flowSession) {
              setFlowSession(normalizeFlowSession(followResp.data.flowSession))
            } else if (follow.nextNodeId) {
              setFlowSession(prev =>
                prev ? { ...prev, currentNodeId: follow.nextNodeId } : prev
              )
            }

            const followMessage = combineMessageWithLink(
              follow.message,
              (follow as any)?.openUrl
            )
            if (hasDisplayContent(followMessage, follow.attachments)) {
              emitFlowMessage(
                followMessage ?? '',
                follow.attachments,
                follow.paymentRequest
              )
            }
            if (follow.menuOptions && follow.menuOptions.length > 0) {
              onMenuOptions?.(follow.message || '', follow.menuOptions)
            }

            first = follow
            safety += 1

            if (!canAutoProgress(first)) break
          } catch (apErr) {
            console.warn('⚠️ [FLOW] Auto-progression after reset failed', apErr)
            break
          }
        }
      }
    } catch (error) {
      console.error('Failed to reset flow:', error)
      setFlowError('Failed to reset flow')
    }
  }, [
    canAutoProgress,
    combineMessageWithLink,
    emitFlowMessage,
    flowOverrideId,
    flowOverrideSlug,
    flowSession,
    hasDisplayContent,
    onMenuOptions,
    onOpenChat,
    sessionId,
    setFlowError,
    setIsFlowActive,
    setFlowSession,
    setHasExecutedEntry,
    setIsFlowCompleting,
    setIsExecutingEntry,
    setLastCompletionTime,
  ])

  // Force check for available flows on component mount to clear stale state
  useEffect(() => {
    // Reset flow state when site changes
    setIsFlowActive(false)
    setFlowSession(null)
    setHasDefaultFlow(null)
    setFlowError(null)

    // Check for available flows and default bot
    checkAvailableFlows()
    checkDefaultBot()
  }, [resolvedSiteId, checkAvailableFlows, checkDefaultBot]) // Only depend on default site ID

  // Ensure we check for flows on mount even if no session exists yet
  useEffect(() => {
    if (!sessionId && hasDefaultFlow === null) {
      checkAvailableFlows()
    }
  }, [sessionId, hasDefaultFlow, checkAvailableFlows])

  // Initialize flow when sessionId changes
  useEffect(() => {
    if (!sessionId) {
      return
    }

    // Check if this is actually a new session ID
    const isNewSessionId = sessionId !== lastSessionId

    if (isNewSessionId) {
      // New chat session detected: clear any stale flow state tied to previous session
      setIsFlowActive(false)
      setFlowSession(null)
      setHasExecutedEntry(false) // Reset entry flag for truly new sessions
      setIsExecutingEntry(false) // Reset execution flag for new sessions
      // Reset ref for new session
      entryExecutionRef.current = {
        isExecuting: false,
        executedForSession: null,
      }
      setLastSessionId(sessionId)

      // Always reset these flags for any session initialization
      setIsFlowCompleting(false) // Reset completion flag
      setLastCompletionTime(0) // Reset completion time
    }

    if (isUserRegistered) {
      initializeFlow()
    }
  }, [sessionId, isUserRegistered, initializeFlow, lastSessionId])

  return {
    isFlowActive,
    flowSession,
    isProcessingFlow,
    isFlowCompleting,
    flowError,
    hasDefaultFlow,
    defaultBot,
    executeFlow,
    resetFlow,
    initializeFlow,
    clearError: () => setFlowError(null),
  }
}
